import React, { FC, useState } from 'react';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { CaretDownFill, CaretRightFill } from 'react-bootstrap-icons';

import { ConfirmModal } from '../../../../../components';
import { CloseCircle } from '../../../../../assets/icons/CloseCircle';
import {
  getBracketCellHeaderSuffix,
  getBracketCellMatchupLabel,
} from '../../../../../utils/bracketCellDisplayLabels';
import { BracketCellGroup } from '../../../../../utils/groupMatchesByBracketCell';
import { recordBracketCellRemoval } from '../../../../../utils/bracketCellPendingDeletes.util';
import type { PlayValues } from '../PlayTag/PlayTag';
import type { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import { Court, MatchResult, Team } from '../../../../../types/tournament';
import { BracketOptionsEnum, ClassTypeEnum } from '../../../../../types/enums';
import { BracketCellEditor } from '../BracketCellEditor/BracketCellEditor';
import { BracketCellHeaderActions } from '../BracketCellEditor/BracketCellHeaderActions';
import { BracketCellSortableMatches } from '../BracketCellEditor/BracketCellSortableMatches';

interface BracketCellSeriesProps {
  group: BracketCellGroup;
  formikArg: FormikProps<PlayValues>;
  courts: Court[];
  currentClass: ClassValues | undefined;
  handleAddTeam: (matchId: string | number, fieldName: string) => void;
  handleAddTeamForBracketCell?: (bracketCellId: string, fieldName: string) => void;
  handleAddPlayer: (
    teamId: string | number,
    fieldName: string,
    matchId: string | number,
  ) => void;
  handleOpenResultModal: (
    results?: MatchResult[],
    matchId?: number | string,
    homeTeam?: Team | null,
    awayTeam?: Team | null,
  ) => void;
  matchesMap: PlayValues['tournamentMatches'];
  variant?: 'group' | 'elimination';
  groupName?: string;
  onPlayDataRefresh?: () => Promise<void>;
}

const bracketSideLabelKey = (bracket: number): string | null => {
  if (bracket === BracketOptionsEnum.Winner) {
    return 'enum-bracketOptionsWinner';
  }
  if (bracket === BracketOptionsEnum.Consolation) {
    return 'enum-bracketOptionsConsolation';
  }
  return null;
};

export const BracketCellSeries: FC<BracketCellSeriesProps> = ({
  group,
  formikArg,
  courts,
  currentClass,
  handleAddTeam,
  handleAddTeamForBracketCell,
  handleAddPlayer,
  handleOpenResultModal,
  matchesMap,
  variant = 'elimination',
}) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const buttonPrefix = 'button-';
  const primary = group.matches[0];
  const isTeamsClass = currentClass?.type === ClassTypeEnum.TEAMS;
  const showCellLevelTeams =
    isTeamsClass && group.matches.length > 0 && !!handleAddTeamForBracketCell;
  const [isExpanded, setIsExpanded] = useState(false);

  const matchupLabel = getBracketCellMatchupLabel(primary);
  const headerSuffix = getBracketCellHeaderSuffix(primary, variant);
  const bracketSideKey = bracketSideLabelKey(Number(primary.bracket));
  const isGroupVariant = variant === 'group';

  const renderHeaderSuffix = () => {
    if (!headerSuffix) {
      return null;
    }
    if (headerSuffix.startsWith('round:')) {
      const roundNumber = headerSuffix.slice('round:'.length);
      return (
        <span className="text-muted ms-2">{t('enum-matchTypeRound', { roundNumber })}</span>
      );
    }
    return <span className="text-muted ms-2">{headerSuffix}</span>;
  };

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  return (
    <Card className="mb-3 border-secondary">
      <Card.Header
        className="py-2 bg-light"
        role="button"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t(prefix + 'bracketCellCollapse') : t(prefix + 'bracketCellExpand')}
        onClick={toggleExpanded}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {isExpanded ? (
              <CaretDownFill size={14} aria-hidden />
            ) : (
              <CaretRightFill size={14} aria-hidden />
            )}
            <div>
              {matchupLabel ? (
                <>
                  <strong>{matchupLabel}</strong>
                  {renderHeaderSuffix()}
                  {!isGroupVariant && bracketSideKey ? (
                    <span className="text-muted ms-2 small">{t(bracketSideKey)}</span>
                  ) : null}
                </>
              ) : (
                <>
                  <strong>{t(prefix + 'bracketCellLabel')}</strong>
                  {renderHeaderSuffix()}
                  {!isGroupVariant && bracketSideKey ? (
                    <span className="text-muted ms-2 small">{t(bracketSideKey)}</span>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div
            className="d-flex flex-wrap align-items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ConfirmModal
              text={t('confirm-deleteBracketCell')}
              onConfirm={() => {
                recordBracketCellRemoval(formikArg, group.cellId);
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`delete-cell-${group.cellId}`}>
                    {t(buttonPrefix + 'deleteBracketCell')}
                  </Tooltip>
                }
              >
                <Button
                  type="button"
                  variant="outline-dark"
                  size="sm"
                  className="d-inline-flex align-items-center justify-content-center p-1"
                  style={{ minWidth: 0, minHeight: 0 }}
                  aria-label={t(buttonPrefix + 'deleteBracketCell')}
                >
                  <CloseCircle />
                </Button>
              </OverlayTrigger>
            </ConfirmModal>
          </div>
        </div>
      </Card.Header>
      {isExpanded ? (
        <Card.Body className="py-2">
          <div className="d-flex align-items-start gap-2" data-testid="matchup-expanded-row">
            <div className="flex-grow-1">
              <BracketCellEditor
                group={group}
                currentClass={currentClass}
                variant={variant}
                showCellLevelTeams={showCellLevelTeams}
                handleAddTeamForBracketCell={handleAddTeamForBracketCell}
              />
            </div>
            <BracketCellHeaderActions
              group={group}
              formikArg={formikArg}
              tournamentCategories={formikArg.values.tournamentCategories}
              currentClass={currentClass}
              variant={variant}
            />
          </div>
          <BracketCellSortableMatches
            group={group}
            formikArg={formikArg}
            courts={courts}
            currentClass={currentClass}
            handleAddTeam={handleAddTeam}
            handleAddPlayer={handleAddPlayer}
            handleOpenResultModal={handleOpenResultModal}
            matchesMap={matchesMap}
            variant={variant}
            hideTeamPickers={showCellLevelTeams}
          />
        </Card.Body>
      ) : null}
    </Card>
  );
};
